import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { serverPHP, time_waitingroom_default, time_redaction_default, time_evaluation_default } from '../const/consts';
import { debug_mode } from '../const/consts';
import { postJSON, NetworkError, getAdaptiveTimeout } from '../composants/NetworkHandler';
import ErrorHandler from '../composants/ErrorHandler';

const LOCAL_STORAGE_KEY = 'sessionParams';

const NouvelleSession = ({ user, setModalContent, setModalTitle, setShowModal, setMotDePasse }) => {
  // ... (états useState inchangés) ...
  const [sujet, setSujet] = useState('');
  const [errors, setErrors] = useState({});
  const [creationError, setCreationError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 2;

  const [sessionType, setSessionType] = useState('une_redaction');
  const [phases, setPhases] = useState(2);
  const [noLimitPhases, setNoLimitPhases] = useState(false);
  const [parts, setParts] = useState(3);
  const [subParts, setSubParts] = useState(2);
  const [usePassword, setUsePassword] = useState(false);
  const [password, setPassword] = useState('');
  const [redactionType, setRedactionType] = useState('longues');
  const [customizeSession, setCustomizeSession] = useState(false);
  const [timeSettings, setTimeSettings] = useState({
    waitingRoom: { minutes: time_waitingroom_default, noLimit: false },
    redaction: { minutes: time_redaction_default, noLimit: false },
    evaluation: { minutes: time_evaluation_default, noLimit: false }
  });

  const isSubmitting = useRef(false);
  const navigate = useNavigate();
  
  // ... (useEffect et autres fonctions inchangées jusqu'à buildPayload) ...
  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !isSubmitting.current) {
      handleCreateSession();
    }
  };

  useEffect(() => {
    const savedParams = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (savedParams) {
      try {
        const parsedParams = JSON.parse(savedParams);
        setSessionType(parsedParams.sessionType || 'une_redaction');
        setPhases(parsedParams.phases || 2);
        setNoLimitPhases(parsedParams.noLimitPhases || false);
        setParts(parsedParams.parts || 3);
        setSubParts(parsedParams.subParts || 2);
        setUsePassword(false);
        setPassword('');
        setRedactionType(parsedParams.redactionType || 'longues');
        setCustomizeSession(false);
        setTimeSettings(parsedParams.timeSettings || {
          waitingRoom: { minutes: time_waitingroom_default, noLimit: false },
          redaction: { minutes: time_redaction_default, noLimit: false },
          evaluation: { minutes: time_evaluation_default, noLimit: false }
        });
      } catch (e) {
        console.warn('Erreur lors du chargement des paramètres sauvegardés:', e);
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      }
    }
  }, []);

  useEffect(() => {
    const params = {
      sessionType,
      phases,
      noLimitPhases,
      parts,
      subParts,
      usePassword: false,
      password: '',
      redactionType,
      timeSettings,
    };
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(params));
    } catch (e) {
      console.warn('Impossible de sauvegarder les paramètres:', e);
    }
  }, [sessionType, phases, noLimitPhases, parts, subParts, redactionType, timeSettings]);

  const handleInfoClick = (message) => {
    setModalContent(<div className="text-left">{message}</div>);
    setModalTitle("ℹ️ Information");
    setShowModal(true);
  };

  useEffect(() => {
    if (sessionType === 'plan') {
      setRedactionType('courtes');
    } else if (sessionType === 'une_redaction' || sessionType === 'plusieurs_redactions') {
      setRedactionType('longues');
    }
  }, [sessionType]);

  const validateForm = () => {
     const newErrors = {};

    if (!sujet.trim()) {
      newErrors.sujet = "Le sujet ne peut pas être vide.";
    } else if (sujet.trim().length > 200) {
      newErrors.sujet = "Le sujet ne peut pas dépasser 200 caractères.";
    }

    if (usePassword && !password.trim()) {
      newErrors.password = "Veuillez entrer un mot de passe ou désactiver cette option.";
    }

    ['waitingRoom', 'redaction', 'evaluation'].forEach((phase) => {
      if (!timeSettings[phase].noLimit) {
        const minutes = parseInt(timeSettings[phase].minutes, 10);
        if (isNaN(minutes) || minutes < 1) {
          newErrors[phase] = "La durée doit être d'au moins 1 minute.";
        }
        if (minutes > 120) {
          newErrors[phase] = "La durée ne peut pas dépasser 2 heures.";
        }
      }
    });

    setErrors(newErrors);
    return newErrors;
  };


  const buildPayload = () => {
    let type = sessionType === 'plan' ? 'plan' : 'redaction';
    let parametres;
    
    if (type === 'redaction') {
      parametres = { phases: noLimitPhases ? 0 : phases };
      if (sessionType === 'une_redaction') {
        parametres.phases = 1;
      }
    } else if (type === 'plan') {
      parametres = { parties: parts, sousparties: subParts };
    }

    let payload = {
      admin: user.id,
      sujet: sujet.trim(),
      type,
      parametres: JSON.stringify(parametres)
    };

    // --- CORRECTION CI-DESSOUS ---
    if (customizeSession) {
      payload.password = password.trim() || "";
      payload.redaction_longue = redactionType === 'longues';
      // On convertit les minutes en secondes avant l'envoi
      payload.time_waitingroom = timeSettings.waitingRoom.noLimit ? 0 : timeSettings.waitingRoom.minutes * 60;
      payload.time_redaction = timeSettings.redaction.noLimit ? 0 : timeSettings.redaction.minutes * 60;
      payload.time_evaluation = timeSettings.evaluation.noLimit ? 0 : timeSettings.evaluation.minutes * 60;
    } else {
      // Les valeurs par défaut sont aussi en minutes, il faut les convertir
      payload.password = "";
      payload.redaction_longue = type === 'redaction';
      payload.time_waitingroom = time_waitingroom_default * 60;
      payload.time_redaction = time_redaction_default * 60;
      payload.time_evaluation = time_evaluation_default * 60;
    }

    return payload;
  };

  const handleCreateSession = async () => {
    if (isSubmitting.current) return;

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      const firstErrorKey = Object.keys(validationErrors)[0];
      const errorMessage = validationErrors[firstErrorKey];
      handleInfoClick(errorMessage);
      return;
    }

    isSubmitting.current = true;
    setCreationError(null);

    try {
      const payload = buildPayload();
      
      if (debug_mode) console.log('Payload de création (en secondes):', payload);

      const timeout = getAdaptiveTimeout(15000);
      
      const data = await postJSON(
        `${serverPHP}createSession.php`,
        payload,
        { timeout, retries: 1, retryDelay: 2000 }
      );

      if (data.session_id) {
        setMotDePasse(password.trim());
        navigate("/session/" + data.session_id);
      } else {
        throw new NetworkError('INVALID_RESPONSE', 'Réponse du serveur invalide', 'Aucun ID de session retourné');
      }

    } catch (error) {
      console.error('Erreur lors de la création de la session:', error);
      let errorType = 'UNKNOWN_ERROR';
      let errorMessage = 'Erreur inattendue lors de la création';
      let errorDetails = error.message;

      if (error instanceof NetworkError) {
        switch (error.type) {
          case 'TIMEOUT': errorType = 'TIMEOUT'; errorMessage = 'La création de la session prend trop de temps'; errorDetails = 'Le serveur met plus de temps que prévu à répondre.'; break;
          case 'NETWORK_ERROR': errorType = 'NETWORK_ERROR'; errorMessage = 'Problème de connexion réseau'; errorDetails = 'Vérifiez votre connexion internet et réessayez.'; break;
          case 'SERVER_ERROR': errorType = 'SERVER_ERROR'; errorMessage = 'Erreur du serveur lors de la création'; errorDetails = 'Le serveur rencontre des difficultés techniques.'; break;
          default: errorMessage = error.message || 'Erreur lors de la création de la session'; errorDetails = error.details;
        }
      }

      setCreationError({ type: errorType, message: errorMessage, details: errorDetails });
    } finally {
      isSubmitting.current = false;
    }
  };
  
  // ... (le reste du composant est inchangé) ...
  const handleRetryCreation = () => {
    if (retryCount < maxRetries) {
      setRetryCount(prev => prev + 1);
      setCreationError(null);
      
      setTimeout(() => {
        handleCreateSession();
      }, 1000 * (retryCount + 1));
    }
  };
  
  const handleGoHome = () => {
    navigate('/');
  };

  const toggleAdvancedSettings = () => {
    setCustomizeSession(!customizeSession);
  };
  
  const sessionTypeOptions = [
    { id: 'une_redaction', emoji: '📄', title: 'Une seule phase', description: 'Une rédaction suivie d\'une évaluation', info: "C'est le type de session le plus simple : les rédacteurs composent un texte, les évaluateurs votent, et la session se termine en déterminant le texte qui a été le plus apprécié." },
    { id: 'plusieurs_redactions', emoji: '🔄', title: 'Plusieurs phases', description: 'Alternance rédaction/évaluation', info: "Dans ce type de session, on enchaîne plusieurs phases de rédaction/évaluation. Il est possible de choisir un nombre illimité de phases ; dans ce cas, c'est soit l'administrateur qui décide de mettre fin à la session, soit les rédacteurs qui arrêtent de contribuer." },
    { id: 'plan', emoji: '📊', title: 'Construction de plan', description: 'Élaboration progressive d\'un plan structuré', info: "Dans ce type de session, on spécifie un certain nombre de parties et de sous-parties, pour donner forme à un plan. Chaque partie et chaque sous-partie donne lieu à une phase de rédaction/évaluation." }
  ];

  const writingOptions = [
    { id: 'courtes', emoji: '💬', title: 'Une phrase', description: 'Réponses courtes (150 caractères max)', info: "Avec le réglage \"une phrase\", les textes rédigés par les utilisateurs sont limités à 150 caractères. C'est idéal pour construire un plan." },
    { id: 'longues', emoji: '📃', title: 'Rédactions longues', description: 'Textes développés sans limite', info: "Dans les rédactions longues, les textes rédigés par les utilisateurs n'ont pas de limite de caractères. Ils ne pourront cependant pas être affichés en entier lors des synthèses de la session." }
  ];

  if (creationError) {
    return (
      <ErrorHandler
        error={creationError}
        onRetry={retryCount < maxRetries ? handleRetryCreation : null}
        onGoHome={handleGoHome}
        showRetry={retryCount < maxRetries}
        customActions={retryCount >= maxRetries ? [
          { text: '🔄 Recommencer', action: () => { setCreationError(null); setRetryCount(0); }, primary: true },
          { text: '🏠 Retour à l\'accueil', action: handleGoHome, secondary: true }
        ] : null}
      />
    );
  }

  return (
    <div className="fade-in">
      <div className="card">
        <h2 className="card-title">✨ Créer une nouvelle session</h2>
        
        <div className="form-group">
          <label className="form-label">📝 Sujet de la session</label>
          <input type="text" className={`form-input ${errors.sujet ? 'border-danger' : ''}`} value={sujet} onChange={(e) => setSujet(e.target.value)} onKeyDown={handleKeyDown} placeholder="Ex: Synthèse du cours sur la photosynthèse" maxLength={200} />
          {sujet.length > 150 && (<div className="text-sm text-warning mt-1">{200 - sujet.length} caractères restants</div>)}
        </div>

        <div className="form-group">
          <label className="form-label">🎯 Type de session</label>
          <div className="session-types">
            {sessionTypeOptions.map((option) => (
              <div key={option.id} className={`session-type-tile ${sessionType === option.id ? 'selected' : ''}`} onClick={() => setSessionType(option.id)}>
                <div className="tile-emoji">{option.emoji}</div>
                <div className="tile-title">{option.title}</div>
                <div className="tile-description">{option.description}</div>
                <button className="btn btn-ghost btn-sm mt-2" onClick={(e) => { e.stopPropagation(); handleInfoClick(option.info); }}>ℹ️ Info</button>
              </div>
            ))}
          </div>
        </div>

        {sessionType === 'plusieurs_redactions' && (
          <div className="form-group">
            <label className="form-label">🔢 Nombre de phases</label>
            <div className="form-row">
              <div className="form-group"><input type="number" className="form-input" value={phases} onChange={(e) => setPhases(Math.max(2, Number(e.target.value)))} disabled={noLimitPhases} min="2" max="20" /></div>
              <div className="no-limit-toggle"><input type="checkbox" id="noLimitPhases" checked={noLimitPhases} onChange={() => setNoLimitPhases(!noLimitPhases)} /><label htmlFor="noLimitPhases">Pas de limite</label></div>
            </div>
          </div>
        )}

        {sessionType === 'plan' && (
          <div className="form-group">
            <label className="form-label">📋 Structure du plan</label>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Parties</label>
                <select value={parts} onChange={(e) => setParts(Number(e.target.value))} className="form-select">
                  {[1, 2, 3, 4].map((num) => (<option key={num} value={num}>{num}</option>))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Sous-parties</label>
                <select value={subParts} onChange={(e) => setSubParts(Number(e.target.value))} className="form-select">
                  {[0, 1, 2, 3, 4].map((num) => (<option key={num} value={num}>{num}</option>))}
                </select>
              </div>
            </div>
          </div>
        )}

        <div className={`advanced-toggle ${customizeSession ? 'active' : ''}`} onClick={toggleAdvancedSettings}>
          <span>{customizeSession ? '▼' : '▶'}</span>
          <span>⚙️ Paramètres avancés</span>
        </div>

        {customizeSession && (
          <div className={`advanced-settings ${customizeSession ? 'open' : ''}`}>
            <div className="form-group">
              <label className="form-label">🔒 Mot de passe (optionnel)</label>
              <div className="form-row">
                <div className="no-limit-toggle"><input type="checkbox" id="usePassword" checked={usePassword} onChange={() => setUsePassword(!usePassword)} /><label htmlFor="usePassword">Protéger par mot de passe</label></div>
              </div>
              {usePassword && (<input type="password" className={`form-input ${errors.password ? 'border-danger' : ''}`} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Entrez le mot de passe" maxLength={50} />)}
            </div>

            <div className="form-group">
              <label className="form-label">✏️ Type de rédaction</label>
              <div className="session-types">
                {writingOptions.map((option) => (
                  <div key={option.id} className={`session-type-tile ${redactionType === option.id ? 'selected' : ''}`} onClick={() => setRedactionType(option.id)}>
                    <div className="tile-emoji">{option.emoji}</div>
                    <div className="tile-title">{option.title}</div>
                    <div className="tile-description">{option.description}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">⏱️ Temps d'attente avant l'étape suivante</label>
              <div className="time-settings-grid">
                {[ { key: 'waitingRoom', icon: '⏳', title: 'Salle d\'attente' }, { key: 'redaction', icon: '✍️', title: 'Rédaction' }, { key: 'evaluation', icon: '⭐', title: 'Évaluation' } ].map((phase) => (
                  <div key={phase.key} className="time-setting-card">
                    <div className="time-setting-header"><div className="time-setting-title"><span className="time-icon">{phase.icon}</span><span className="time-title">{phase.title}</span></div></div>
                    <div className="time-controls">
                      <div className="time-input-group">
                        <input type="number" className={`form-input time-input ${errors[phase.key] ? 'border-danger' : ''}`} value={timeSettings[phase.key].minutes} onChange={(e) => { const value = e.target.value; setTimeSettings({ ...timeSettings, [phase.key]: { ...timeSettings[phase.key], minutes: value } }); }} disabled={timeSettings[phase.key].noLimit} min="1" max="120" placeholder="Minutes" />
                        <span className="time-unit">{timeSettings[phase.key].noLimit ? '' : 'min'}</span>
                      </div>
                      <div className="no-limit-toggle">
                        <input type="checkbox" id={`${phase.key}NoLimit`} checked={timeSettings[phase.key].noLimit} onChange={() => { setTimeSettings({ ...timeSettings, [phase.key]: { ...timeSettings[phase.key], noLimit: !timeSettings[phase.key].noLimit } }); }} />
                        <label htmlFor={`${phase.key}NoLimit`}>Pas de limite</label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="form-actions">
          <button className="btn btn-large btn-primary create-session-btn" onClick={handleCreateSession}>
            <span>🚀</span>
            <span>Créer la session</span>
          </button>
        </div>

        {retryCount > 0 && (
          <div className="text-center mt-3">
            <p className="text-xs text-secondary">Tentative {retryCount + 1}/{maxRetries + 1}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NouvelleSession;